import Header from '@/components/Header';
import { TanstackDevtools } from '@tanstack/react-devtools';
import { createRootRouteWithContext, Link, Outlet, useMatches } from '@tanstack/react-router';
import { TanStackRouterDevtoolsPanel } from '@tanstack/react-router-devtools';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@ui/components/breadcrumb';
import { InputGroup, InputGroupAddon, InputGroupInput } from '@ui/components/input-group';
import { Kbd } from '@ui/components/kbd';
import { ModeToggle } from '@ui/components/mode-toggle';
import { Separator } from '@ui/components/separator';
import { SidebarInset, SidebarTrigger } from '@ui/components/sidebar';
import { SearchIcon } from 'lucide-react';
import type { AppContext } from '../main';

export const Route = createRootRouteWithContext<AppContext>()({
  component: RootComponent,
  loader: () => ({
    crumb: 'Home',
  }),
});

function RootComponent() {
  const matches = useMatches();

  const breadcrumbItems = matches
    .filter((match) => match.loaderData?.crumb)
    .map(({ pathname, loaderData }) => ({
      href: pathname,
      label: loaderData?.crumb,
    }))
    .filter(
      (item, index, array) =>
        array.findIndex((existingItem) => existingItem.label === item.label) === index
    );

  return (
    <>
      <Header />
      <SidebarInset className="flex h-screen w-full flex-col overflow-hidden">
        <header className="flex shrink-0 items-center justify-between gap-2 border-b p-3 py-2">
          <div className="flex items-center gap-2 py-2">
            <SidebarTrigger />
            <Separator orientation="vertical" className="mx-2 data-[orientation=vertical]:h-4" />

            <Breadcrumb>
              <BreadcrumbList>
                {breadcrumbItems.map((item, index) => (
                  <div key={btoa(item.label + item.href)} className="flex items-center">
                    {index < breadcrumbItems.length - 1 ? (
                      <>
                        <BreadcrumbItem>
                          <Link to={item.href} className="hover:text-foreground transition-colors">
                            {item.label}
                          </Link>
                        </BreadcrumbItem>
                        <BreadcrumbSeparator />
                      </>
                    ) : (
                      <BreadcrumbItem>
                        <BreadcrumbPage>{item.label}</BreadcrumbPage>
                      </BreadcrumbItem>
                    )}
                  </div>
                ))}
              </BreadcrumbList>
            </Breadcrumb>
          </div>{' '}
          <div className="flex items-center gap-4">
            <InputGroup>
              <InputGroupInput placeholder="Search..." />
              <InputGroupAddon>
                <SearchIcon />
              </InputGroupAddon>
              <InputGroupAddon align={'inline-end'}>
                <Kbd>⌘ + K</Kbd>
              </InputGroupAddon>
            </InputGroup>
            <ModeToggle />
          </div>
        </header>
        <div className="min-h-0 flex-1 overflow-hidden">
          <Outlet />
        </div>

        <TanstackDevtools
          config={{
            position: 'bottom-left',
          }}
          plugins={[
            {
              name: 'Tanstack Router',
              render: <TanStackRouterDevtoolsPanel />,
            },
          ]}
        />
      </SidebarInset>
    </>
  );
}
